/**
 * Carrossel de Imagens
 * Multi-slide carousel — 4 images on desktop, 2 on tablet, 1 on mobile
 */

interface CarrosselState {
  currentIndex: number;
  totalSlides: number;
  slidesPerView: number;
  maxIndex: number;
}

class CarrosselImagens {
  private carousel: HTMLElement;
  private track: HTMLElement;
  private prevBtn: HTMLButtonElement;
  private nextBtn: HTMLButtonElement;
  private dotsContainer: HTMLElement;
  private state: CarrosselState;
  private autoPlayInterval?: ReturnType<typeof setInterval>;

  constructor(carousel: HTMLElement) {
    this.carousel      = carousel;
    this.track         = carousel.querySelector('.carrossel-imagens-track') as HTMLElement;
    this.prevBtn       = carousel.parentElement?.querySelector('.carrossel-imagens-prev') as HTMLButtonElement;
    this.nextBtn       = carousel.parentElement?.querySelector('.carrossel-imagens-next') as HTMLButtonElement;
    this.dotsContainer = carousel.parentElement?.querySelector('.carrossel-imagens-dots') as HTMLElement;

    if (!this.track || !this.prevBtn || !this.nextBtn || !this.dotsContainer) return;

    const slides = this.track.querySelectorAll('.carrossel-imagens-slide');

    this.state = {
      currentIndex: 0,
      totalSlides: slides.length,
      slidesPerView: this.getSlidesPerView(),
      maxIndex: 0,
    };

    this.calculateMaxIndex();
    this.init();
  }

  private getSlidesPerView(): number {
    const width = window.innerWidth;
    if (width >= 1024) return 4; // lg: 4 images
    if (width >= 768)  return 2; // md: 2 images
    return 1;                    // mobile: 1 image
  }

  private calculateMaxIndex(): void {
    this.state.maxIndex = Math.max(0, this.state.totalSlides - this.state.slidesPerView);
  }

  private init(): void {
    this.createDots();

    this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn.addEventListener('click', () => this.next());

    // Resize handler
    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const prev = this.state.slidesPerView;
        this.state.slidesPerView = this.getSlidesPerView();
        if (prev !== this.state.slidesPerView) {
          this.calculateMaxIndex();
          this.state.currentIndex = Math.min(this.state.currentIndex, this.state.maxIndex);
          this.createDots();
          this.updateCarousel();
        }
      }, 250);
    });

    if (this.carousel.dataset.autoplay === '1') this.startAutoPlay();

    this.updateCarousel();
  }

  private createDots(): void {
    this.dotsContainer.innerHTML = '';
    const totalDots = this.state.maxIndex + 1;

    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'w-2 h-2 rounded-full transition-all';
      dot.setAttribute('aria-label', `Ir para slide ${i + 1}`);
      dot.addEventListener('click', () => {
        this.state.currentIndex = i;
        this.updateCarousel();
        this.resetAutoPlay();
      });
      this.dotsContainer.appendChild(dot);
    }
  }

  private updateCarousel(): void {
    const percentage = -(this.state.currentIndex * (100 / this.state.slidesPerView));
    this.track.style.transform = `translateX(${percentage}%)`;

    this.prevBtn.disabled = this.state.currentIndex === 0;
    this.nextBtn.disabled = this.state.currentIndex >= this.state.maxIndex;
    this.prevBtn.style.opacity = this.state.currentIndex === 0 ? '0.5' : '1';
    this.nextBtn.style.opacity = this.state.currentIndex >= this.state.maxIndex ? '0.5' : '1';

    const dots = this.dotsContainer.querySelectorAll('button');
    dots.forEach((dot, index) => {
      if (index === this.state.currentIndex) {
        dot.classList.add('bg-custom-1', 'w-8');
        dot.classList.remove('bg-gray-3');
      } else {
        dot.classList.add('bg-gray-3');
        dot.classList.remove('bg-custom-1', 'w-8');
      }
    });
  }

  private prev(): void {
    if (this.state.currentIndex > 0) {
      this.state.currentIndex--;
      this.updateCarousel();
      this.resetAutoPlay();
    }
  }

  private next(): void {
    if (this.state.currentIndex < this.state.maxIndex) {
      this.state.currentIndex++;
    } else {
      this.state.currentIndex = 0;
    }
    this.updateCarousel();
    this.resetAutoPlay();
  }

  private startAutoPlay(): void {
    this.autoPlayInterval = setInterval(() => {
      if (this.state.currentIndex >= this.state.maxIndex) {
        this.state.currentIndex = 0;
      } else {
        this.state.currentIndex++;
      }
      this.updateCarousel();
    }, 4500);
  }

  private resetAutoPlay(): void {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.startAutoPlay();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll<HTMLElement>('.carrossel-imagens').forEach((carousel) => {
    new CarrosselImagens(carousel);
  });
});
